import {
  Checkbox,
  ListItem as MuiListItem,
  ListItemButton as MuiListItemButton,
  Radio,
  type SxProps,
  type Theme,
} from "@mui/material";
import { mergeSxProps } from "@src/appV2/lib";
import { type ReactNode } from "react";

type SelectionMode = "radio" | "checkbox";
type IndicatorPosition = "left" | "right";

export interface SelectableListItemProps {
  children: ReactNode;
  isSelected: boolean;
  onSelect: () => void;
  selectionMode: SelectionMode;
  disabled?: boolean;
  indicatorPosition?: IndicatorPosition;
  sx?: SxProps<Theme>;
}

/**
 * A selectable list item with radio or checkbox indicator.
 * Extends the ManageListItem pattern with selection functionality.
 */
export function SelectableListItem(props: SelectableListItemProps) {
  const {
    children,
    isSelected,
    onSelect,
    selectionMode,
    disabled = false,
    indicatorPosition = "left",
    sx,
  } = props;

  const SelectionIndicator =
    selectionMode === "radio" ? (
      <Radio
        checked={isSelected}
        disabled={disabled}
        size="small"
        sx={(theme) => ({
          padding: 0,
          color: theme.palette.border?.base,
          "&.Mui-checked": {
            color: theme.palette.primary.main,
          },
        })}
      />
    ) : (
      <Checkbox
        checked={isSelected}
        disabled={disabled}
        size="small"
        sx={(theme) => ({
          padding: 0,
          color: theme.palette.border?.base,
          "&.Mui-checked": {
            color: theme.palette.primary.main,
          },
        })}
      />
    );

  return (
    <MuiListItem disablePadding>
      <MuiListItemButton
        disabled={disabled}
        sx={mergeSxProps(
          (theme: Theme) => ({
            display: "flex",
            flexDirection: indicatorPosition === "left" ? "row" : "row-reverse",
            alignItems: "center",
            gap: 3,
            paddingY: 3,
            paddingX: 4,
            borderRadius: 0,
            "&:hover": {
              backgroundColor: theme.palette.background.muted,
            },
            ...(isSelected && {
              backgroundColor: theme.palette.background.muted,
            }),
          }),
          sx
        )}
        onClick={onSelect}
      >
        {SelectionIndicator}
        {children}
      </MuiListItemButton>
    </MuiListItem>
  );
}
